import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import {
  RelayEnvironmentProvider,
  useFragment,
  useRelayEnvironment,
} from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { JoinWaitlistIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { useFlags } from "../../../utils/launchDarkly";
import { Waitlist } from "./Waitlist";
import { WaitlistModalFragment_Facility$key } from "./__generated__/WaitlistModalFragment_Facility.graphql";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  date: string;
  facilityId: string;
  courseIds: string[] | null;
  fragmentRef: WaitlistModalFragment_Facility$key;
  onJoinWaitlist: (queueSlotId: string, requestedTime: string) => void;
};

export const WaitlistModal = ({
  date,
  facilityId,
  isOpen,
  onClose,
  courseIds,
  fragmentRef,
  onJoinWaitlist,
}: Props) => {
  const facility = useFragment(WaitlistModalFragmentString, fragmentRef);
  const agenda = facility.agendas?.edges?.[0]?.node;

  const relayEnvironment = useRelayEnvironment();

  const { waitlistRestrictions: waitlistRestrictionsFeatureOn } = useFlags();

  const hasWaitlistRestrictions = agenda?.waitlistRestrictions?.edges?.length;

  const queueSlotId = agenda?.queueSlot.id;

  const handleJoinWaitlist = () => {
    onClose();
    onJoinWaitlist(queueSlotId ?? "", "");
  };

  const waitlistRestrictions =
    waitlistRestrictionsFeatureOn && hasWaitlistRestrictions;

  return (
    <StandardModal
      upperTitle={t`Waitlist`}
      isOpen={isOpen}
      onClose={onClose}
      upperNode={
        waitlistRestrictions ? (
          <Button
            variant="secondary"
            accessibilityLabel={t`Join Waitlist button`}
            onPress={handleJoinWaitlist}
            leftIcon={<JoinWaitlistIcon />}
          >
            {t`Join`}
          </Button>
        ) : undefined
      }
      content={
        <RelayEnvironmentProvider environment={relayEnvironment}>
          <Box px={6}>
            <Waitlist
              date={date}
              facilityId={facilityId}
              alwaysOpen={true}
              courseIds={courseIds}
            />
          </Box>
        </RelayEnvironmentProvider>
      }
    />
  );
};

export const WaitlistModalFragmentString = graphql`
  fragment WaitlistModalFragment_Facility on Facility {
    agendas(dateGte: $date, dateLte: $date) {
      edges {
        node {
          queueSlot {
            id
          }
          waitlistRestrictions: bookingRules(type: [WAITLIST]) {
            edges {
              liftAt
              node {
                id
              }
            }
          }
        }
      }
    }
  }
`;
